import { useState, useEffect, useCallback } from 'react';

/**
 * Hook personnalisé pour gérer le filtre de site web avec localStorage
 * Synchronise automatiquement les changements entre tous les composants qui utilisent le même storageKey
 * @param {string} storageKey - Clé pour stocker dans localStorage (default: 'AC_Dashboard_SelectedWebsite')
 * @returns {Array} [selectedWebsiteUrl, setSelectedWebsiteUrl]
 */
export const useWebsiteFilter = (storageKey = 'AC_Dashboard_SelectedWebsite') => {
  const [selectedWebsiteUrl, setSelectedWebsiteUrlState] = useState(() => {
    try {
      return window.localStorage.getItem(storageKey) || '';
    } catch (e) {
      return '';
    }
  });

   useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === storageKey || e.key === null) {
        try {
          const newValue = window.localStorage.getItem(storageKey) || '';
          setSelectedWebsiteUrlState((prev) => {
            if (prev !== newValue) {
              return newValue;
            }
            return prev;
          });
        } catch (e) {
          // Ignore localStorage errors
        }
      }
    };

    // Écouter l'événement storage (pour les changements depuis d'autres onglets)
    window.addEventListener('storage', handleStorageChange);

    // Écouter un événement personnalisé pour les changements dans le même onglet
    const eventName = `websiteFilterChange_${storageKey}`;
    const handleCustomEvent = (e) => {
      if (e.detail && e.detail.value !== undefined) {
        const newValue = e.detail.value || '';
        setSelectedWebsiteUrlState((prev) => {
          if (prev !== newValue) {
            return newValue;
          }
          return prev;
        });
      }
    };
    window.addEventListener(eventName, handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(eventName, handleCustomEvent);
    };
  }, [storageKey]);

  // Fonction pour mettre à jour le site sélectionné avec synchronisation localStorage
  const setSelectedWebsiteUrl = useCallback((value) => {
    const newValue = value || '';
    setSelectedWebsiteUrlState((prev) => {
      if (prev === newValue) {
        return prev; // Pas de changement nécessaire
      }

      try {
        if (newValue) {
          window.localStorage.setItem(storageKey, newValue);
        } else {
          window.localStorage.removeItem(storageKey);
        }

        // Déclencher un événement personnalisé pour notifier les autres composants dans le même onglet
        const eventName = `websiteFilterChange_${storageKey}`;
        window.dispatchEvent(new CustomEvent(eventName, { detail: { value: newValue } }));
      } catch (e) {
        // Ignore localStorage errors
      }

      return newValue;
    });
  }, [storageKey]);

  return [selectedWebsiteUrl, setSelectedWebsiteUrl];
};

